import { useMemo } from 'react'
import Select, { components } from 'react-select'
import { Checkbox } from '@bosch/react-frok'
import type { ControlProps, OptionProps, GroupBase, MultiValueRemoveProps } from 'react-select'

// styles
import './FrokMultiSelect.scss'

export type FrokSelectOption = { value: string; label: string }

type Group = GroupBase<FrokSelectOption>
type CtrlProps = ControlProps<FrokSelectOption, true, Group>
type OptProps = OptionProps<FrokSelectOption, true, Group>
type RemoveProps = MultiValueRemoveProps<FrokSelectOption, true, Group>

const HiddenIndicatorsContainer = () => null

export interface FrokMultiSelectProps {
    options: FrokSelectOption[]
    value: FrokSelectOption[]
    onChange: (values: FrokSelectOption[]) => void
    label?: string
    showLabel?: boolean
    placeholder?: string
    withCheckboxes?: boolean
    className?: string
    isDisabled?: boolean
    noOptionsMessage?: string
    /** Position the menu with `fixed`, so it is not clipped by a scrolling ancestor */
    fixedMenu?: boolean
}

// The Bosch close icon instead of the cross of react-select
const BoschMultiValueRemove = (props: RemoveProps) => (
    <components.MultiValueRemove
        {...props}
        innerProps={{
            ...props.innerProps,
            onTouchEnd: (event) => {
                event.preventDefault()
                event.stopPropagation()
                props.innerProps.onTouchEnd?.(event)
            },
        }}
    >
        <i className="a-icon boschicon-bosch-ic-close fms__remove-icon" aria-hidden="true" />
    </components.MultiValueRemove>
)

// The Bosch checkbox shows the state only: the option selects (react-select), and the checkbox lets the clicks through to it
const CheckboxOption = (props: OptProps) => (
    <components.Option {...props}>
        <Checkbox
            id={`${props.innerProps.id ?? props.data.value}-checkbox`}
            className="fms__checkbox"
            label={props.label}
            checked={props.isSelected}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            // a click on the label clicks the input too: that second click must not select the option again
            onClick={(event) => event.stopPropagation()}
        />
    </components.Option>
)

const FrokMultiSelect = ({
    options,
    value,
    onChange,
    label,
    showLabel = true,
    placeholder,
    withCheckboxes = false,
    className,
    isDisabled = false,
    noOptionsMessage,
    fixedMenu = false,
}: FrokMultiSelectProps) => {

    const displayLabel = !!label && showLabel
    const CustomControl = useMemo(() => {
        const Inner = ({ className: cls, innerProps, innerRef, isFocused, menuIsOpen, ...props }: CtrlProps) => {
            return (
                <div
                    {...innerProps}
                    ref={innerRef}
                    className={`${cls} fms__control-wrapper${menuIsOpen ? ' fms__control-wrapper--menu-is-open' : ''}${isFocused || menuIsOpen ? ' fms__control-wrapper--is-focused' : ''}`}
                >
                    <div
                        className={[
                            displayLabel ? 'fms__control--with-label' : '',
                            'fms__control',
                            isFocused || menuIsOpen ? 'fms__control--is-focused' : ''
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {displayLabel && (
                            <span className="fms__field-label" aria-hidden="true">
                                {label}
                            </span>
                        )}
                        <div className="fms__control-row">{props.children}</div>
                    </div>
                    <div className="fms__custom-indicator">
                        <i className="a-icon boschicon-bosch-ic-down fms__arrow-icon" aria-hidden="true" />
                    </div>
                </div>
            )
        }
        Inner.displayName = 'FmsControl'
        return Inner
    }, [displayLabel, label])

    const customComponents = useMemo(
        () => ({
            Control: CustomControl,
            IndicatorsContainer: HiddenIndicatorsContainer,
            MultiValueRemove: BoschMultiValueRemove,
            ...(withCheckboxes ? { Option: CheckboxOption } : {}),
        }),
        [CustomControl, withCheckboxes]
    )

    return (
        <div className={`frok-multi-select${className ? ` ${className}` : ''}`}>
            {label && (
                <span className="frok-multi-select__sr-label">
                    {label}
                </span>
            )}
            <Select<FrokSelectOption, true, Group>
                isMulti
                options={options}
                value={value}
                onChange={(newValue) => onChange(newValue ? [...newValue] : [])}
                placeholder={placeholder}
                isDisabled={isDisabled}
                unstyled
                classNamePrefix="fms"
                components={customComponents}
                closeMenuOnSelect={!withCheckboxes}
                blurInputOnSelect={withCheckboxes ? false : undefined}
                hideSelectedOptions={false}
                isClearable
                menuPosition={fixedMenu ? 'fixed' : 'absolute'}
                menuPlacement={fixedMenu ? 'auto' : 'bottom'}
                // the wrapper of a fixed menu must stack above the rows' absolutely positioned borders (react-select sets 1)
                styles={fixedMenu ? { menuPortal: (base) => ({ ...base, zIndex: 200 }) } : undefined}
                noOptionsMessage={noOptionsMessage ? () => noOptionsMessage : undefined}
            />
        </div>
    )
}

export default FrokMultiSelect
